import { useId, useState, type ReactNode } from 'react';
import { BarChart3, ChevronDown } from 'lucide-react';
import { cn } from '@/shared/utils/utils';

/**
 * Collapsible home for a page's charts. The redesign puts each module's work
 * queue first and moves analytics into a secondary view; this keeps every
 * chart one click away (and remembers the viewer's choice per page) without
 * pushing the register below the fold.
 */
export function AnalyticsPanel({
  storageKey,
  title = 'Analytics',
  description,
  defaultOpen = false,
  children,
  className,
}: {
  /** Stable per-page key, e.g. "sales-orders". */
  storageKey: string;
  title?: string;
  description?: string;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const key = `rbm.analytics.${storageKey}`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(key);
      return v === null ? defaultOpen : v === '1';
    } catch {
      return defaultOpen;
    }
  });
  const regionId = useId();
  const toggle = () => {
    setOpen((o) => {
      try { localStorage.setItem(key, o ? '0' : '1'); } catch { /* storage unavailable */ }
      return !o;
    });
  };
  return (
    <section className={cn('rounded-card border border-outline bg-surface', className)}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={regionId}
        className="flex w-full items-center gap-3 px-5 py-3 text-left"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <BarChart3 className="h-4 w-4" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-display text-[15px] font-semibold text-on-surface">{title}</span>
          {description && <span className="block text-body-xs text-on-surface-subtle">{description}</span>}
        </span>
        <span className="text-body-sm font-medium text-primary">{open ? 'Hide' : 'Show'}</span>
        <ChevronDown className={cn('h-4 w-4 text-on-surface-variant transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open && (
        <div id={regionId} className="border-t border-outline p-4">
          {children}
        </div>
      )}
    </section>
  );
}
